import { getRecruitingInterviewDataThunk, scoreRecruitingInterviewThunk } from "../store/exam/thunks";
import { useEffect, useState } from "react";
import { useCountDown } from "./useCountDown";
import { RecruitingInterviewData } from "../store/exam/types";
import { useDispatch, useSelector } from "../store/store";
import { setNotification } from "../store/general/slice";
import { setRecruitingInterviewStep } from "../store/exam/slice";
import { useVideoCapture } from "./useVideoCapture";
import { useLocation } from "react-router-dom";
import { uploadFileThunk } from "../store/general/thunks";
import { selectUserId } from "../store/auth/selectors";
import { selectProfileData } from "../store/workspace/selectors";
import { getProfileDataThunk } from "../store/workspace/thunks";
import { getUserInfoThunk } from "../store/auth/thunks";

export const useRecruitingInterview = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const userId = useSelector(selectUserId);
    const profile = useSelector(selectProfileData);
    const [isExamEnded, setIsExamEnded] = useState(false);
    const { counter, pause, restart } = useCountDown(300, 'start');
    const [currentQuestion, setCurrentQuestion] = useState(1);
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [totalInterviewDuration, setTotalInterviewDuration] = useState(0);
    const [interviewData, setInterviewData] = useState<RecruitingInterviewData | null>(null);
    const isLastQuestion = interviewData?.countQuestions === currentQuestion;
    const totalQuestions = interviewData?.countQuestions || 0;
    const jobId = location?.state?.jobId;
    const action = location?.state?.action;

    const {
        webcamRef,
        streamFile,
        stopCapture,
        chunkNumber,
        setStreamCount,
        startCapture,
        streamFileName,
    } = useVideoCapture();

    useEffect(() => {
        !userId && dispatch(getUserInfoThunk());
        !profile && dispatch(getProfileDataThunk());
    }, []);

    useEffect(() => {
        uploadStreamChunk();
    }, [streamFile]);

    const updateUploadProgress = (percentage: number) => {
        setFileUploadProgress(percentage)
    };

    const uploadStreamChunk = async () => {
        if (!streamFile) return;
        await dispatch(uploadFileThunk({
            fileName: `${userId}/${jobId}/interview/${chunkNumber}.webm`,
            file: streamFile,
            callback: updateUploadProgress
        }));

        if (isExamEnded) {
            dispatch(scoreRecruitingInterviewThunk({
                jobId,
                interviewId: interviewData?.id,
                length: totalInterviewDuration,
            }))
                .unwrap()
                .then(() => {
                    dispatch(setRecruitingInterviewStep('InterviewEnd'));
                })
                .catch(() => {
                    setIsFetchingData(false);
                    dispatch(setNotification(['An error occured, please try again.', 'error']));
                })
        }
    };

    const startInterview = () => {
        setIsFetchingData(true);
        dispatch(getRecruitingInterviewDataThunk({
            jobId,
            pre: 1,
            action,
        }))
            .unwrap()
            .then(res => {
                restart();
                setInterviewData(res);

                if (action === 'resume' && res.questionNumber) {
                    setStreamCount(res.questionNumber - 1);
                    setCurrentQuestion(res.questionNumber);
                }

                startCapture();
                setIsFetchingData(false);
                dispatch(setRecruitingInterviewStep('Interview'));
            })
            .catch(() => {
                setIsFetchingData(false);
                dispatch(setNotification(['An error occured, please try again.', 'error']));
            })
    }

    const nextQuestion = () => {
        pause();
        stopCapture();
        setTotalInterviewDuration(prev => prev + (300 - counter));

        if (isLastQuestion) {
            setIsExamEnded(true);
            dispatch(setRecruitingInterviewStep('CollectingData'));
        } else {
            setIsFetchingData(true);
            dispatch(getRecruitingInterviewDataThunk({
                jobId,
                pre: currentQuestion + 1,
            }))
                .unwrap()
                .then(res => {
                    startCapture();
                    setInterviewData(res);
                    setIsFetchingData(false);
                    setCurrentQuestion(prev => prev + 1);
                    restart();
                })
                .catch(() => {
                    setIsFetchingData(false);
                    dispatch(setNotification(['An error occured, please try again.', 'error']));
                })
        }
    }

    return {
        jobId,
        userId,
        action,
        profile,
        counter,
        webcamRef,
        nextQuestion,
        interviewData,
        isFetchingData,
        startInterview,
        totalQuestions,
        currentQuestion,
        fileUploadProgress,
    }
};